import { useWatch, FieldValues, Path } from 'react-hook-form';
import { useDelegateAdHocFormFreeTextInputOptions } from '../../../../../../../../hooks/confirmPurchase/delegate/useDelegateAdHocFormFreeTextInputOptions';
import { PricingFormInputOption } from '../../../../../../../../types';
import { PricingFormInputLayout } from '../../../../../../../../types/enums';
import { MuiGrid } from '../../../../../../../../../../modules/material';
import CheckboxGroupField, {
  CheckboxGroupFieldProps,
} from '../../../../../../../../shared/formElements/CheckboxGroupField';
import CheckboxField from '../../../../../../../../shared/formElements/CheckboxField';
import AdHocFreeTextField from '../AdHocFreeTextField';

export interface AdHocCheckboxGroupFieldProps<TFieldValues>
  extends CheckboxGroupFieldProps<TFieldValues> {
  layout?: keyof typeof PricingFormInputLayout;
  options: PricingFormInputOption[];
}

export default function AdHocCheckboxGroupField<
  TFieldValues extends FieldValues,
>(props: AdHocCheckboxGroupFieldProps<TFieldValues>) {
  const { name, helperText, label, layout, options, rules } = props;

  const { freeTextOption, filteredOptions: checkboxOptions } =
    useDelegateAdHocFormFreeTextInputOptions(options);
  const checkboxFreeTextOptionName =
    `${name}.__checkbox__` as Path<TFieldValues>;
  const checkboxFreeTextOptionLabel = freeTextOption?.label;

  const selectedFreeTextValue = useWatch({
    name: checkboxFreeTextOptionName,
    disabled: !freeTextOption,
  }) as boolean;

  const showFreeTextField = !!freeTextOption && selectedFreeTextValue;

  const checkboxGridOptions = {
    xs: 12,
    ...(layout === PricingFormInputLayout.COLUMN && { md: 6 }),
  };

  return (
    <CheckboxGroupField
      name={name}
      label={label}
      helperText={helperText}
      rules={rules}
    >
      <MuiGrid container columnSpacing="8px" rowSpacing="8px">
        {checkboxOptions.map((option) => {
          const nestedOptionName =
            `${name}.${option.value}` as Path<TFieldValues>;

          return (
            <MuiGrid key={nestedOptionName} item {...checkboxGridOptions}>
              <CheckboxField
                name={nestedOptionName}
                groupName={name}
                label={option.label}
                fullWidth={false}
              />
            </MuiGrid>
          );
        })}
        {!!freeTextOption && (
          <MuiGrid item {...checkboxGridOptions}>
            <CheckboxField
              name={checkboxFreeTextOptionName}
              groupName={name}
              label={checkboxFreeTextOptionLabel}
              fullWidth={false}
            />
          </MuiGrid>
        )}
        {showFreeTextField && (
          <MuiGrid item xs={12}>
            <AdHocFreeTextField name={name} />
          </MuiGrid>
        )}
      </MuiGrid>
    </CheckboxGroupField>
  );
}
